<template>
  <b-modal
    :id="modalId"
    v-model="localIsActive"
    :header-bg-variant="headerBgVariant"
    :header-text-variant="headerTextVariant"
    centered
    scrollable
    no-close-on-esc
    no-close-on-backdrop
    content-class="modal-wizard"
    :size="size"
    @hidden="closeModal"
    @shown="loadData"
  >
    <template #modal-title>{{ wizardTitle }} </template>
    <WizardStepIndicator
      :currentStep="currentStep"
      :numberedSteps="steps"
      :stepIcons="stepIcons"
      @step-clicked="goToStep"
      :is-add="isAdd"
      :allow-direct-step-navigation="allowDirectStepNavigation"
    />
    <hr />
    <div class="wizard-content">
      <slot :name="`step-${currentStepKey}`" :currentStep="currentStep" :loading="loading" />
    </div>

    <template #modal-footer>
      <b-button variant="secondary" @click="closeModal" data-cy="wizard-cancel">
        {{ $t("actions.cancel").charAt(0).toUpperCase() + $t("actions.cancel").slice(1) }}
      </b-button>
      <b-button
        variant="secondary"
        @click="emitPrevStep"
        :disabled="currentStep === 0"
        v-if="showPreviousButton"
        data-cy="wizard-prev"
      >
        {{
          $t("actions.previous").charAt(0).toUpperCase() + $t("actions.previous").slice(1)
        }}
      </b-button>
      <b-button
        variant="info"
        @click="saveData"
        v-if="showSaveButton"
        :disabled="!activateBtnSave"
        data-cy="wizard-save"
      >
        <b-spinner small v-if="loading"></b-spinner>
        <span v-else>
          {{
            $t("actions.save_changes").charAt(0).toUpperCase() +
            $t("actions.save_changes").slice(1)
          }}
        </span>
      </b-button>
      <b-button
        variant="primary"
        @click="emitNextStep"
        :disabled="currentStep > finishAtStep"
        v-if="showNextButton"
        data-cy="wizard-next"
      >
        {{ $t("actions.next").charAt(0).toUpperCase() + $t("actions.next").slice(1) }}
      </b-button>
      <b-button variant="success" @click="finishWizard" v-if="showFinishButton" data-cy="finish-wizard-btn">
        {{ $t("actions.finish").charAt(0).toUpperCase() + $t("actions.finish").slice(1) }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { BModal, BSpinner } from "bootstrap-vue";
import WizardStepIndicator from "./WizardStepIndicator.vue";
import { computed, ref, onBeforeUnmount, watch } from "@vue/composition-api";
import store from "@/store";

export default {
  components: {
    BModal,
    BSpinner,
    WizardStepIndicator,
  },
  props: {
    modalId: { type: String, required: true },
    isActive: { type: Boolean, required: true },
    wizardTitle: { type: String, default: "Wizard" },
    steps: { type: Array, required: true },
    stepIcons: { type: Array, required: true },
    isAdd: { type: Boolean, required: true, default: false },
    allowDirectStepNavigation: { type: Boolean, default: false },
    finishAtStep: { type: Number, default: 3 },
    currentStep: { type: Number, required: true },
    currentStepKey: { type: String, default: ""},
    showPreviousButton: { type: Boolean, default: true },
    showSaveButton: { type: Boolean, default: true },
    showNextButton: { type: Boolean, default: true },
    showFinishButton: { type: Boolean, default: true },

    activateBtnSave: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },

    hasConfig: { type: Boolean, default: true },
    size: {
      type: String,
      default: 'lg'
    }
  },
  setup(props, { emit }) {
    const localLoading = ref(props.loading);

    const localIsActive = computed({
      get() {
        return props.isActive;
      },
      set(value) {
        emit("update:isActive", value);
      },
    });

    onBeforeUnmount(() => {
      store.commit("chatbot/updateCanBeMaximized", true);
    });

    const closeModal = () => {
      localIsActive.value = false;
      emit("close-wizard");
      emit("reset-wizard");
      store.commit("chatbot/updateCanBeMaximized", true);
    };

    const emitNextStep = () => {
      if (props.currentStep < props.finishAtStep) {
        emit("next-step");
      }
    };

    const emitPrevStep = () => {
      if (props.currentStep > 0) {
        emit("prev-step");
      }
    };

    const finishWizard = () => {
      emit("on-submit");
    };

    const goToStep = (index) => {
      if (props.allowDirectStepNavigation || index <= props.currentStep) {
        emit("go-to-step", index);
      }
    };

    const loadData = () => {
      emit("load-data");
    };

    const saveData = () => {
      localLoading.value = true;
      emit("save-data");
    };

    watch(
      () => props.loading,
      (newValue) => {
        localLoading.value = newValue;
      }
    );

    return {
      localLoading,
      emitNextStep,
      emitPrevStep,
      finishWizard,
      goToStep,
      headerBgVariant: "dark",
      headerTextVariant: "light",
      loadData,
      saveData,
      closeModal,
      localIsActive,
    };
  },
};
</script>

<style lang="scss">
.modal-title {
  color: #fff;
}
.wizard-content {
  height: 60vh;
  overflow-y: auto;
}
.modal-header .bg-dark {
  background-color: var(--main-dark) !important;
}
</style>
